import { Button, Card, MonoLabel, ProgressBar } from '@/design-system';
import type { InspectorOccupancy } from '@/entities/order';
import { CardTitleRow } from '@/shared/ui';

import { dashboardCopy } from '../copy';

export interface OccupancyCardProps {
    rows: readonly InspectorOccupancy[];
    /** Naar de planning, voor wie mag plannen: een percentage zonder vervolg is decoratie. */
    onOpenPlanning?: () => void;
}

/**
 * Bezetting per inspecteur: naam 96 px, track 8 px, percentage in mono.
 *
 * De bezetting gaat over de uren **bij dit bedrijf**. Zonder ingestelde
 * beschikbaarheid is het percentage onbekend - dan staat er geen balk. Wie mag
 * plannen, gaat met de knop in de kop naar de planning om er iets mee te doen.
 */
export function OccupancyCard({ rows, onOpenPlanning }: OccupancyCardProps) {
    return (
        <Card clip>
            <CardTitleRow
                title={dashboardCopy.occupancyTitle}
                action={
                    onOpenPlanning ? (
                        <Button variant="ghost" onClick={onOpenPlanning}>
                            {dashboardCopy.occupancyAction}
                        </Button>
                    ) : null
                }
            />
            <div className="p-4">
                <p className="m-0 mb-[14px] text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {dashboardCopy.occupancyNote}
                </p>

                {rows.length === 0 ? (
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {dashboardCopy.occupancyEmpty}
                    </p>
                ) : (
                    rows.map((row) => (
                        <div key={row.userUuid} className="mb-[11px] flex items-center gap-[10px]">
                            <span className="w-24 truncate text-[13px] text-[var(--text-primary)]">
                                {row.name}
                            </span>
                            {row.percentage === null ? (
                                <span className="flex-1 text-[12px] text-[var(--text-tertiary-safe)]">
                                    {dashboardCopy.occupancyUnknown}
                                </span>
                            ) : (
                                <>
                                    <ProgressBar
                                        className="flex-1"
                                        value={Math.min(100, row.percentage)}
                                        label={dashboardCopy.occupancyLabel(row.name)}
                                    />
                                    <MonoLabel
                                        size={11}
                                        variant="value"
                                        tone="default"
                                        className="w-[38px] text-right"
                                    >
                                        {row.percentage}%
                                    </MonoLabel>
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>
        </Card>
    );
}
